import { useEffect, useRef, useState, useCallback } from "react";
import { ShareExpiry } from "@/components/ShareExpiry";
import { Sparkles } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReklamationLePanel } from "@/components/le/ReklamationLePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ImageAnnotator, { AnnotationLayer } from "@/components/ImageAnnotator";
import { ArrowLeft, Type, ImagePlus, ListChecks, CheckCircle2, XCircle, Trash2, ArrowUp, ArrowDown, Plus, Upload, Pencil, Check, CloudUpload, Loader2, SlidersHorizontal, Share2, Copy, RefreshCw, ExternalLink, Camera, HardDrive, Paperclip, FileText } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NONE = "__none__";
const TYPE_LABEL = { intern_qc: "Intern QC", reklamation: "Reklamation", indkob_reklamation: "Indkøbsreklamation" };
const DEPARTMENTS = ["Boring", "Drejebænk", "Forsendelse", "Fræs", "Håndsvejs", "Indkøb", "Kantpres (lille)", "Kantpres (stor)", "Laserskærer", "Leverandør", "Maler", "Montage", "Produktion", "Robotsvejs", "Rørbuk", "Rørlaser", "Salg", "Sav", "Slib", "Udvikling"];
const FAULT_CATS = ["Dokumentation", "Materiale", "Montage", "Mål/tolerance", "Overflade", "Transport"];
const ANDET = "__andet__";
const INDKOB_FAULT_CATS = ["Overflade", "Tolerancer/mål", "Forkert mængde"];
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const daDate = (s) => { const d = String(s || "").slice(0, 10); if (d.length === 10) { const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`; } return "—"; };
const LEVELS = [
  { key: "Mindre", color: "#A8A29E" },
  { key: "Væsentlig", color: "#A08979" },
  { key: "Kritisk", color: "#A83232" },
];
const STATUS_BY_TYPE = { intern_qc: ["Kladde", "I gang", "Færdig"], reklamation: ["Kladde", "Godkendt", "Afvist"] };
const REK_REQUIRED = [
  { key: "item_no", label: "Varenr." },
  { key: "complaint_no", label: "Reklamationsnr." },
  { key: "department", label: "Afdeling" },
  { key: "fault_category", label: "Fejlkategori" },
];
const STATUS_TONE = { Godkendt: "#7E856C", "Færdig": "#7E856C", Afvist: "#A83232", "I gang": "#A08979", Kladde: "#78716C" };
const BLOCK_TYPES = [
  { type: "text", label: "Tekst", icon: Type },
  { type: "image", label: "Billede", icon: ImagePlus },
  { type: "defects", label: "Årsager", icon: ListChecks },
  { type: "conclusion", label: "Konklusion", icon: CheckCircle2 },
];

export default function QualityReportBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [deptOther, setDeptOther] = useState(false);
  const [fcOther, setFcOther] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [editImg, setEditImg] = useState(null); // block id being annotated
  const [confirmDel, setConfirmDel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [revNote, setRevNote] = useState("");
  const [analyser, setAnalyser] = useState([]);
  const [warnVarenr, setWarnVarenr] = useState(null);
  const warnedFor = useRef(null);
  const timer = useRef(null);
  const driveInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const canEdit = report?.can_edit;

  useEffect(() => {
    if (!["reklamation", "indkob_reklamation"].includes(report?.type)) { setAnalyser([]); return; }
    api.get(`/reklamation-analyser/for-report/${id}`).then((r) => setAnalyser(r.data || [])).catch(() => {});
  }, [report?.type, id]);

  useEffect(() => {
    if (!["reklamation", "indkob_reklamation"].includes(report?.type)) return;
    const v = (report?.item_no || "").trim();
    if (!v) return;
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/reklamation-count?varenr=${encodeURIComponent(v)}&exclude=${id}&rtype=${report.type}`);
        if (data.count >= 3 && warnedFor.current !== v) { warnedFor.current = v; setWarnVarenr(v); }
      } catch (_) {}
    }, 800);
    return () => clearTimeout(h);
  }, [report?.item_no, report?.type, id]);

  useEffect(() => {
    api.get(`/quality-reports/${id}`).then((r) => setReport(r.data)).catch(() => { toast.error("Rapport ikke fundet"); nav("/reklamationer"); });
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, [id, nav]);

  useEffect(() => {
    if (!report) return;
    setDeptOther(!!(report.department || "").trim() && !DEPARTMENTS.includes(report.department));
    const _fcList = report.type === "indkob_reklamation" ? INDKOB_FAULT_CATS : FAULT_CATS;
    setFcOther(!!(report.fault_category || "").trim() && !_fcList.includes(report.fault_category));
  }, [report?.id]); // eslint-disable-line

  const pending = useRef({});
  const inflight = useRef(false);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    if (inflight.current) return;
    const patch = pending.current;
    if (!Object.keys(patch).length) { setSaveState("saved"); return; }
    pending.current = {};
    inflight.current = true;
    setSaveState("saving");
    api.patch(`/quality-reports/${id}`, patch)
      .catch((e) => { pending.current = { ...patch, ...pending.current }; toast.error("Kunne ikke gemme", { description: formatApiError(e.response?.data?.detail) }); })
      .finally(() => { inflight.current = false; if (Object.keys(pending.current).length) flush(); else setSaveState("saved"); });
  }, [id]);

  const schedule = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  const commit = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch };
    flush();
  }, [flush]);

  const setMeta = (k, v) => { setReport((r) => ({ ...r, [k]: v })); schedule({ [k]: v }); };

  const setDocImages = (updater) => setReport((r) => { const arr = typeof updater === "function" ? updater(r.doc_images || []) : updater; schedule({ doc_images: arr }); return { ...r, doc_images: arr }; });
  const setDocDefects = (defects) => { setReport((r) => ({ ...r, doc_defects: defects })); schedule({ doc_defects: defects }); };
  const updateDocImage = (iid, patch) => setDocImages((imgs) => imgs.map((im) => im.id === iid ? { ...im, ...patch } : im));
  const removeDocImage = (iid) => setDocImages((imgs) => imgs.filter((im) => im.id !== iid));
  const moveDocImage = (iid, dir) => setDocImages((imgs) => {
    const i = imgs.findIndex((im) => im.id === iid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= imgs.length) return imgs;
    const next = [...imgs];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const dragFrom = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const reorderDocImages = (from, to) => setDocImages((imgs) => {
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= imgs.length || to >= imgs.length) return imgs;
    const next = [...imgs];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  });
  const chooseStatus = (status) => {
    if (["reklamation", "indkob_reklamation"].includes(report?.type) && (status === "Godkendt" || status === "Afvist")) {
      const rf = report?.type === "indkob_reklamation" ? REK_REQUIRED.filter((f) => f.key !== "department").map((f) => f.key === "complaint_no" ? { ...f, label: "Indkøbsnr." } : f) : REK_REQUIRED;
      const miss = rf.filter((f) => !String(report[f.key] || "").trim());
      if (miss.length) { toast.error("Udfyld påkrævede felter før afslutning", { description: miss.map((f) => f.label).join(", ") }); return; }
    }
    setReport((r) => ({ ...r, status }));
    if (["Godkendt", "Afvist", "Færdig"].includes(status)) {
      const patch = { ...pending.current, status }; pending.current = {};
      api.patch(`/quality-reports/${id}`, patch).then(() => { toast.success(`Status sat til ${status}`); nav(report?.type === "indkob_reklamation" ? "/indkoeb/reklamationer" : "/reklamationer"); })
        .catch((e) => toast.error("Kunne ikke gemme", { description: formatApiError(e.response?.data?.detail) }));
      return;
    }
    commit({ status });
  };

  const onCustomer = (cid) => {
    const c = [...customers, ...suppliers].find((x) => x.id === cid);
    setReport((r) => ({ ...r, customer_id: cid, customer_name: c?.name || "", customer_contact: null }));
    commit({ customer_id: cid, customer_name: c?.name || "", customer_contact: { name: "", email: "", phone: "" } });
  };

  const uploadDocImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/quality-reports/${id}/images`, fd);
      const newIds = data.ids || (data.id ? [data.id] : []);
      setDocImages((imgs) => [...imgs, ...newIds.map((iid) => ({ id: uid(), image_id: iid, caption: "", annotations: [] }))]);
      toast.success(newIds.length > 1 ? `${newIds.length} sider tilføjet` : "Billede uploadet");
    } catch (e) { toast.error("Upload fejlede", { description: formatApiError(e.response?.data?.detail) }); }
  };

  const doDelete = () => api.delete(`/quality-reports/${id}`).then(() => { toast.success("Rapport slettet"); nav("/kvalitetsrapporter"); });

  const bumpRevision = () => api.post(`/quality-reports/${id}/revision`, { note: revNote.trim() }).then((r) => {
    setReport((prev) => ({ ...prev, revision: r.data.revision, revision_date: r.data.revision_date, responsible_name: r.data.responsible_name, revision_history: [...(prev.revision_history || []), r.data.entry] }));
    toast.success(`Version ${r.data.revision} oprettet`, { description: "Historik opdateret på delingssiden." });
    setRevOpen(false); setRevNote("");
  }).catch((e) => toast.error("Kunne ikke oprette version", { description: formatApiError(e.response?.data?.detail) }));

  const shareUrl = report?.share_token ? `${window.location.origin}/kvalitetsrapport-kunde/${report.share_token}` : "";
  const createShare = () => api.post(`/quality-reports/${id}/share`).then((r) => setReport((p) => ({ ...p, share_token: r.data.share_token }))).catch(() => toast.error("Kunne ikke oprette link"));
  const regenShare = () => api.post(`/quality-reports/${id}/share/regenerate`).then((r) => { setReport((p) => ({ ...p, share_token: r.data.share_token })); toast.success("Nyt link genereret"); }).catch(() => toast.error("Kunne ikke generere nyt link"));
  const deleteShare = () => api.delete(`/quality-reports/${id}/share`).then(() => { setReport((p) => ({ ...p, share_token: null })); toast.success("Link deaktiveret"); }).catch(() => toast.error("Kunne ikke deaktivere link"));
  const copyShare = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Link kopieret"); };
  const openShare = () => { setShareOpen(true); if (!report.share_token) createShare(); };

  if (!report) return <div className="text-stone-400 text-sm py-20 text-center">Indlæser…</div>;
  const imgUrl = (imageId) => `${BACKEND}/api/quality-reports/${id}/images/${imageId}`;
  const editingImage = (report.doc_images || []).find((im) => im.id === editImg);
  const ro = !canEdit;
  const isRek = report.type === "reklamation";
  const isIndkob = report.type === "indkob_reklamation";
  const isReklamation = isRek || isIndkob;
  const listPath = isIndkob ? "/indkoeb/reklamationer" : "/reklamationer";
  const reqFields = isIndkob ? REK_REQUIRED.filter((f) => f.key !== "department").map((f) => f.key === "complaint_no" ? { ...f, label: "Indkøbsnr." } : f) : REK_REQUIRED;
  const missingReq = isReklamation ? reqFields.filter((f) => !String(report[f.key] || "").trim()) : [];

  const renderPanel = (pfx = "") => (
    <>
      <div className="pcard p-5 space-y-4">
        <p className="label-caps">Detaljer</p>
        {isReklamation && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kunderef"><Input value={report.customer_ref || ""} disabled={ro} onChange={(e) => setMeta("customer_ref", e.target.value)} placeholder="Kundens ref." className="rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}qr-customer-ref`} /></Field>
            <Field label={isIndkob ? "Indkøbsnr." : "Reklamationsnr"} required filled={!!(report.complaint_no || "").trim()}><Input value={report.complaint_no || ""} disabled={ro} onChange={(e) => setMeta("complaint_no", e.target.value)} placeholder="fra ERP" className="rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}qr-complaint-no`} /></Field>
          </div>
        )}
        {isReklamation && (
          <div className="grid grid-cols-2 gap-3">
            {!isIndkob && <Field label="Afdeling" required filled={!!(report.department || "").trim()}>
              <Select value={deptOther ? ANDET : (DEPARTMENTS.includes(report.department) ? report.department : NONE)} disabled={ro}
                onValueChange={(v) => { if (v === ANDET) { setDeptOther(true); setMeta("department", ""); } else { setDeptOther(false); setMeta("department", v === NONE ? "" : v); } }}>
                <SelectTrigger data-testid={`${pfx}qr-department`} className="rounded-xl bg-white border-[#EBE8E3]"><SelectValue placeholder="Vælg" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Vælg…</SelectItem>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}<SelectItem value={ANDET}>Andet (skriv selv)</SelectItem></SelectContent>
              </Select>
              {deptOther && <Input value={report.department || ""} disabled={ro} onChange={(e) => setMeta("department", e.target.value)} placeholder="Skriv afdeling…" className="mt-2 rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}qr-department-other`} />}
            </Field>}
            <Field label="Fejlkategori" required filled={!!(report.fault_category || "").trim()}>
              <Select value={fcOther ? ANDET : ((isIndkob ? INDKOB_FAULT_CATS : FAULT_CATS).includes(report.fault_category) ? report.fault_category : NONE)} disabled={ro}
                onValueChange={(v) => { if (v === ANDET) { setFcOther(true); setMeta("fault_category", ""); } else { setFcOther(false); setMeta("fault_category", v === NONE ? "" : v); } }}>
                <SelectTrigger data-testid={`${pfx}qr-fault-cat`} className="rounded-xl bg-white border-[#EBE8E3]"><SelectValue placeholder="Vælg" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Vælg…</SelectItem>{(isIndkob ? INDKOB_FAULT_CATS : FAULT_CATS).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}<SelectItem value={ANDET}>Andet (skriv selv)</SelectItem></SelectContent>
              </Select>
              {fcOther && <Input value={report.fault_category || ""} disabled={ro} onChange={(e) => setMeta("fault_category", e.target.value)} placeholder="Skriv fejlkategori…" className="mt-2 rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}qr-fault-cat-other`} />}
            </Field>
          </div>
        )}
        <Field label="Varenr." required={isReklamation} filled={!!(report.item_no || "").trim()}><Input value={report.item_no || ""} disabled={ro} onChange={(e) => setMeta("item_no", e.target.value)} placeholder="fx 13990" className="rounded-xl bg-white border-[#EBE8E3]" data-testid={`${pfx}qr-item-no`} /></Field>
        <Field label={isIndkob ? "Leverandør" : "Kunde"}>
          <Select value={report.customer_id || NONE} disabled={ro} onValueChange={(v) => onCustomer(v === NONE ? "" : v)}>
            <SelectTrigger data-testid={`${pfx}qr-customer`} className="rounded-xl bg-white border-[#EBE8E3]"><SelectValue placeholder={isIndkob ? "Ingen leverandør" : "Ingen kunde"} /></SelectTrigger>
            <SelectContent><SelectItem value={NONE}>{isIndkob ? "Ingen leverandør" : "Ingen kunde"}</SelectItem>{(isIndkob ? suppliers : customers).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="pcard p-5 space-y-3">
        <p className="label-caps">Status</p>
        <div className="grid grid-cols-3 gap-2">
          {(STATUS_BY_TYPE[report.type] || STATUS_BY_TYPE.intern_qc).map((s) => {
            const active = report.status === s;
            const tone = STATUS_TONE[s] || "#78716C";
            return (
              <button key={s} data-testid={`${pfx}qr-status-${s}`} disabled={ro} onClick={() => chooseStatus(s)}
                className={`py-2 rounded-lg text-sm font-medium border transition-colors ${active ? "text-white border-transparent shadow-sm" : "bg-white text-stone-600 border-[#EBE8E3] hover:bg-[#F3F1EC]"}`}
                style={active ? { background: tone } : undefined}>{s}</button>
            );
          })}
        </div>
      </div>

      <div className="pcard p-5 space-y-3">
        <p className="label-caps">Versionsstyring</p>
        <div>
          <p className="text-2xl font-light text-[#1A1919] leading-none" data-testid={`${pfx}qr-revision`}>{report.revision || 1}</p>
          <p className="text-xs text-stone-400 mt-2">Oprettet {daDate(report.revision_date || report.created_at)}</p>
          <p className="text-xs text-stone-400">Ansvarlig: {report.responsible_name || report.created_by_name || "—"}</p>
        </div>
        {(report.revision_history || []).length > 0 && (
          <div className="pt-3 border-t border-[#F3F1EC] space-y-2.5" data-testid={`${pfx}qr-history`}>
            <p className="label-caps">Historik</p>
            {[...report.revision_history].reverse().map((h, i) => (
              <div key={i} className="text-xs">
                <p className="font-semibold text-stone-600">v{h.version} · {daDate(h.at)}</p>
                <p className="text-stone-500 leading-snug">{h.note || "Ingen beskrivelse"}{h.by ? ` — ${h.by}` : ""}</p>
              </div>
            ))}
          </div>
        )}
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setRevOpen(true)} data-testid={`${pfx}qr-new-revision`}
            className="w-full rounded-xl border-[#EBE8E3] text-stone-700 hover:bg-[#F3F1EC]">
            <Plus className="h-4 w-4 mr-1.5" /> Ny version
          </Button>
        )}
      </div>

    </>
  );

  return (
    <div className="rise-in">
      <div className="lg:sticky lg:top-0 z-30 -mx-5 sm:-mx-8 lg:-mx-12 mb-8 px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-3 bg-[#FBFAF8]/85 backdrop-blur-xl border-b border-[#EBE8E3]">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => nav(listPath)} className="-ml-2 text-stone-500 hover:text-stone-800 rounded-xl shrink-0" data-testid="qr-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tilbage
          </Button>
          <span className="hidden sm:block h-5 w-px bg-[#EBE8E3]" />
          <span className="hidden sm:inline text-sm text-stone-400 truncate">QC rapport · {TYPE_LABEL[report.type] || ""}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span data-testid="qr-save-state" className="text-xs text-stone-400 inline-flex items-center gap-1.5 px-1.5">
            {saveState === "saving" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Gemmer…</> : saveState === "dirty" ? <><CloudUpload className="h-3.5 w-3.5" /> Ændringer…</> : <><Check className="h-3.5 w-3.5 text-[#7E856C]" /> Gemt</>}
          </span>
          {canEdit && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden rounded-xl border-[#EBE8E3] text-stone-700" data-testid="qr-panel-trigger"><SlidersHorizontal className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Indstillinger</span></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[360px] max-w-[90vw] overflow-y-auto bg-[#F3F1EC] p-5 space-y-4" data-testid="qr-panel-sheet">
                <p className="label-caps">Værktøjer</p>
                {renderPanel("m-")}
                {isReklamation && <ReklamationLePanel reportId={id} canEscalate={user?.role === "admin" || (user?.departments || []).some((d) => ["kvalitet", "indkoeb"].includes(d))} />}
              </SheetContent>
            </Sheet>
          )}
          {canEdit && <Button variant="outline" size="sm" onClick={openShare} data-testid="qr-share-btn" className="rounded-xl border-[#EBE8E3] text-stone-700 hover:bg-[#F3F1EC]"><Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Del</span></Button>}
          {canEdit && <Button variant="ghost" size="icon" onClick={() => setConfirmDel(true)} data-testid="qr-delete-btn" className="rounded-xl text-[#A83232] hover:bg-[#F1DEDA]"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
        {/* Document canvas */}
        <div className="pcard p-6 sm:p-10 lg:p-14 rounded-2xl max-w-[880px] w-full mx-auto lg:mx-0 min-w-0" data-testid="qr-canvas">
          <div className="flex items-start justify-between gap-4">
            <img src="/bsm-logo.png" alt="BSM A/S" className="h-9 w-auto" />
            <div className="flex flex-col items-end gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400">{TYPE_LABEL[report.type] || "Kvalitetsrapport"}</span>
              <ConclusionBadge report={report} />
            </div>
          </div>
          <div className="mt-8 h-px bg-gradient-to-r from-[#A83232]/50 via-[#EBE8E3] to-transparent" />
          <input data-testid="qr-title" value={report.title || ""} disabled={ro}
            onChange={(e) => setMeta("title", e.target.value)}
            className="w-full mt-7 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1919] bg-transparent outline-none placeholder:text-stone-300 placeholder:font-light"
            placeholder="Rapporttitel" />
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5 pb-8 mb-8 border-b border-[#EBE8E3]" data-testid="qr-meta-line">
            {report.doc_no && <MetaItem label="Reklamationsnr." value={report.doc_no} />}
            {report.type === "reklamation" && <MetaItem label="Kunderef" value={report.customer_ref} />}
            {report.type === "reklamation" && <MetaItem label="Reklamationsnr" value={report.complaint_no} />}
            <MetaItem label="Varenr." value={report.item_no} />
            <MetaItem label="Kunde" value={report.customer_name} />
            <MetaItem label="Dato" value={daDate(report.revision_date || report.created_at)} />
            <MetaItem label="Ansvarlig" value={report.responsible_name || report.created_by_name} />
            <MetaItem label="Version" value={`${report.revision || 1}`} />
          </div>

          <div className="space-y-12">
            {/* Beskrivelse */}
            <section data-testid="qr-section-beskrivelse">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">Beskrivelse</h3>
              <Textarea value={report.description || ""} disabled={ro} onChange={(e) => setMeta("description", e.target.value)} placeholder="Skriv beskrivelse…" className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[90px]" data-testid="qr-description" />
            </section>

            {/* Dokumentation */}
            <section data-testid="qr-section-dokumentation">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">Dokumentation</h3>
              <div className="space-y-4">
                {(report.doc_images || []).map((im, idx, arr) => (
                  <div key={im.id} data-testid="qr-doc-image"
                    onDragOver={canEdit ? (e) => { e.preventDefault(); if (dragOverIdx !== idx) setDragOverIdx(idx); } : undefined}
                    onDrop={canEdit ? () => { reorderDocImages(dragFrom.current, idx); dragFrom.current = null; setDragOverIdx(null); } : undefined}
                    className={`rounded-2xl [transition-property:box-shadow] duration-150 ${dragOverIdx === idx ? "ring-2 ring-[#A83232] ring-offset-2" : ""}`}>
                    <div className="relative rounded-xl overflow-hidden border border-[#EBE8E3] min-h-[120px] bg-[#F3F1EC]"
                      draggable={canEdit}
                      onDragStart={canEdit ? (e) => { dragFrom.current = idx; e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragEnd={() => { dragFrom.current = null; setDragOverIdx(null); }}
                      style={canEdit ? { cursor: "move" } : undefined}>
                      <img src={imgUrl(im.image_id)} alt="" className="block w-full h-auto min-h-[120px] object-contain" />
                      <AnnotationLayer annotations={im.annotations || []} />
                      {canEdit && arr.length > 1 && (
                        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                          <button onClick={() => moveDocImage(im.id, -1)} disabled={idx === 0} data-testid="qr-move-up"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button onClick={() => moveDocImage(im.id, 1)} disabled={idx === arr.length - 1} data-testid="qr-move-down"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                            <ArrowDown className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {canEdit && (
                        <div className="absolute top-2 right-2 flex items-center gap-2">
                          <button onClick={() => setEditImg(im.id)} data-testid="qr-edit-image"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/95 border border-[#EBE8E3] text-stone-700 hover:bg-white">
                            <Pencil className="h-3.5 w-3.5" /> Rediger & marker
                          </button>
                          <button onClick={() => removeDocImage(im.id)} data-testid="qr-remove-image"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white/95 border border-[#EBE8E3] text-[#A83232] hover:bg-[#F1DEDA]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <Textarea value={im.caption || ""} disabled={ro} onChange={(e) => updateDocImage(im.id, { caption: e.target.value })}
                      placeholder="Tilføj tekst til billedet…" className="w-full mt-2 border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[64px] text-sm" data-testid="qr-image-caption" />
                  </div>
                ))}
                {canEdit && (
                  <>
                    <input ref={driveInputRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" data-testid="qr-image-upload" onChange={(e) => { uploadDocImage(e.target.files?.[0]); e.target.value = ""; }} />
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="qr-image-capture" onChange={(e) => { uploadDocImage(e.target.files?.[0]); e.target.value = ""; }} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBE8E3] bg-white text-sm font-medium text-stone-700 cursor-pointer hover:bg-[#F3F1EC] w-fit" data-testid="qr-add-image-btn">
                          <Plus className="h-4 w-4 text-[#A83232]" strokeWidth={2.25} /> Tilføj billede
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-52">
                        <DropdownMenuItem data-testid="qr-add-image-camera" onSelect={() => setTimeout(() => cameraInputRef.current?.click(), 0)}>
                          <Camera className="h-4 w-4 mr-2 text-stone-500" /> Tag billede
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="qr-add-image-drive" onSelect={() => setTimeout(() => driveInputRef.current?.click(), 0)}>
                          <HardDrive className="h-4 w-4 mr-2 text-stone-500" /> Upload fra drev
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-[#F3F1EC]">
                <DefectsBlock defects={report.doc_defects || []} ro={ro} onChange={setDocDefects} />
              </div>
              <div className="mt-6 pt-6 border-t border-[#F3F1EC]">
                <AttachmentsBlock reportId={id} attachments={report.doc_attachments || []} ro={ro} onChange={(list) => setReport((r) => ({ ...r, doc_attachments: list }))} />
              </div>
            </section>

            {/* Korrigerende / Forebyggende (reklamation + indkøbsreklamation) */}
            {isReklamation && (
              <section data-testid="qr-section-korrigerende">
                <h3 className="text-lg font-semibold text-[#1A1919] mb-3">Korrigerende handling</h3>
                <Textarea value={report.corrective_action || ""} disabled={ro} onChange={(e) => setMeta("corrective_action", e.target.value)} placeholder="Beskriv den korrigerende handling…" className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[80px]" data-testid="qr-corrective" />
              </section>
            )}
            {isReklamation && (
              <section data-testid="qr-section-forebyggende">
                <h3 className="text-lg font-semibold text-[#1A1919] mb-3">Forebyggende handling</h3>
                <Textarea value={report.preventive_action || ""} disabled={ro} onChange={(e) => setMeta("preventive_action", e.target.value)} placeholder="Beskriv den forebyggende handling…" className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[80px]" data-testid="qr-preventive" />
              </section>
            )}

            {/* Konklusion */}
            <section data-testid="qr-section-konklusion">
              <h3 className="text-lg font-semibold text-[#1A1919] mb-3">Konklusion</h3>
              <div className="flex gap-3 mb-3">
                {(isReklamation ? [{ key: "Godkendt", color: "#7E856C", tid: "godkendt", Icon: CheckCircle2 }, { key: "Afvist", color: "#A83232", tid: "afvist", Icon: XCircle }]
                        : [{ key: "I gang", color: "#A08979", tid: "igang", Icon: Loader2 }, { key: "Færdig", color: "#7E856C", tid: "faerdig", Icon: CheckCircle2 }]).map((o) => (
                  <button key={o.key} disabled={ro} data-testid={`qr-conclusion-${o.tid}`} onClick={() => chooseStatus(o.key)}
                    className="flex-1 py-3 rounded-xl border font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                    style={report.status === o.key ? { background: o.color, color: "#fff", borderColor: "transparent" } : { background: "#fff", color: "#57534E", borderColor: "#EBE8E3" }}>
                    <o.Icon className="h-4 w-4" /> {o.key}
                  </button>
                ))}
              </div>
              {isReklamation && missingReq.length > 0 && (
                <div className="mb-3 rounded-lg border border-[#E9C9C3] bg-[#FBF1EF] px-3 py-2 text-sm text-[#A83232]" data-testid="qr-required-hint">
                  Udfyld påkrævede felter før rapporten kan godkendes eller afvises: {missingReq.map((f) => f.label).join(", ")}.
                </div>
              )}
              <Textarea value={report.conclusion_note || ""} disabled={ro} onChange={(e) => setMeta("conclusion_note", e.target.value)} placeholder="Begrundelse / bemærkning" className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[70px]" data-testid="qr-conclusion-note" />
            </section>

            {isReklamation && analyser.length > 0 && (
              <section data-testid="qr-section-analyser" className="pt-2">
                <h3 className="text-lg font-semibold text-[#1A1919] mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#6B4FA3]" /> Årsagsanalyser <span className="text-[10px] font-semibold text-[#6B4FA3] bg-[#EDE7F6] border border-[#DDD2EF] rounded-full px-2 py-0.5">AI-drevet</span></h3>
                <div className="space-y-3">
                  {analyser.map((an) => (
                    <details key={an.id} className="rounded-xl border border-[#DDD2EF] bg-[#FAF8FD] p-4" data-testid={`qr-analyse-${an.id}`}>
                      <summary className="cursor-pointer flex items-center justify-between gap-2 list-none">
                        <span className="font-medium text-[#1A1919]">{an.title} <a href={`${an.report_type === "indkob_reklamation" ? "/indkoeb" : "/kvalitet"}/reklamationsanalyse/analyse/${an.id}`} className="text-xs font-medium text-[#A83232] hover:underline ml-2" data-testid={`qr-analyse-open-${an.id}`}>Åbn</a></span>
                        <span className="text-xs text-stone-400">{an.status || "Under vurdering"} · {daDate(an.created_at)} · {an.count} rekl.</span>
                      </summary>
                      <div className="mt-3 space-y-3 text-sm">
                        <p className="text-stone-700">{an.analysis?.summary}</p>
                        <div className="rounded-lg bg-[#FBF1EF] border border-[#E9C9C3] p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-[#A83232] mb-1">Sandsynlig rodårsag</p><p className="text-[#7a3b36]">{an.analysis?.root_cause}</p></div>
                        <ol className="space-y-1.5">{(an.analysis?.five_whys || []).map((w, i) => (<li key={i} className="text-stone-600"><span className="font-medium text-[#1A1919]">{i + 1}. {w.why}</span> <span className="text-stone-500">→ {w.because}</span></li>))}</ol>
                        {(an.active_action || an.override_forebyggende || an.override_korrigerende) && (
                          <div className="rounded-lg border border-[#EBE8E3] bg-white p-3 space-y-1.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Vurdering & handling</p>
                            {an.active_action && <p><span className="font-medium">Aktiv handling:</span> <span className="text-stone-600 whitespace-pre-wrap">{an.active_action}</span></p>}
                            {an.override_forebyggende && <p><span className="font-medium">Forebyggende:</span> <span className="text-stone-600 whitespace-pre-wrap">{an.override_forebyggende}</span></p>}
                            {an.override_korrigerende && <p><span className="font-medium">Korrigerende:</span> <span className="text-stone-600 whitespace-pre-wrap">{an.override_korrigerende}</span></p>}
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Side panel (desktop) */}
        <aside className="hidden lg:block lg:sticky lg:top-[84px] space-y-4">
          {renderPanel("")}
          {isReklamation && <ReklamationLePanel reportId={id} canEscalate={user?.role === "admin" || (user?.departments || []).some((d) => ["kvalitet", "indkoeb"].includes(d))} />}
        </aside>
      </div>

      <Dialog open={!!editImg} onOpenChange={(o) => !o && setEditImg(null)}>
        <DialogContent className="max-w-3xl" data-testid="qr-annotator-dialog">
          <DialogHeader>
            <DialogTitle>Marker på billedet</DialogTitle>
            <DialogDescription>Tilføj pile, kryds, cirkler og markeringer for at fremhæve fejl.</DialogDescription>
          </DialogHeader>
          {editingImage?.image_id && (
            <ImageAnnotator src={imgUrl(editingImage.image_id)} value={editingImage.annotations || []}
              onChange={(annotations) => updateDocImage(editingImage.id, { annotations })} />
          )}
          <div className="flex justify-end pt-2">
            <Button onClick={() => setEditImg(null)} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="qr-annotator-done"><Check className="h-4 w-4 mr-1.5" /> Færdig</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!warnVarenr} onOpenChange={(o) => !o && setWarnVarenr(null)}>
        <DialogContent data-testid="qr-rca-warning">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#A83232]" /> Gentagne reklamationer</DialogTitle>
            <DialogDescription>Varenr. {warnVarenr} er reklameret 4 gange eller mere. Der bør laves en årsagsanalyse for at finde den bagvedliggende årsag.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setWarnVarenr(null)} className="rounded-xl">Ikke nu</Button>
            <Button onClick={() => nav(`${report.type === "indkob_reklamation" ? "/indkoeb" : "/kvalitet"}/reklamationsanalyse?varenr=${encodeURIComponent(warnVarenr)}&auto=1`)} data-testid="qr-rca-warning-open" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Sparkles className="h-4 w-4 mr-1.5" /> Åbn årsagsanalyse</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="qr-share-dialog">
          <DialogHeader>
            <DialogTitle>Del med kunde</DialogTitle>
            <DialogDescription>Send dette link til kunden. Rapporten vises skrivebeskyttet i BSM-design.</DialogDescription>
          </DialogHeader>
          {report.share_token ? (
            <div className="space-y-4 py-1">
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} data-testid="qr-share-url" className="rounded-xl bg-[#FBFAF8] border-[#EBE8E3] text-sm" onFocus={(e) => e.target.select()} />
                <Button onClick={copyShare} data-testid="qr-share-copy" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a] shrink-0"><Copy className="h-4 w-4 mr-1.5" /> Kopiér</Button>
              </div>
              <ShareExpiry kind="report" docId={id} expiresAt={report?.share_expires_at} onChange={(v) => setReport((p) => ({ ...p, share_expires_at: v }))} disabled={ro} />
              <div className="flex items-center justify-between">
                <a href={report?.share_token ? `${window.location.origin}/kvalitetsrapport-kunde/${report.share_token}` : shareUrl} target="_blank" rel="noreferrer" className="text-sm text-[#A83232] hover:underline inline-flex items-center gap-1.5" data-testid="qr-share-open"><ExternalLink className="h-4 w-4" /> Åbn kundevisning</a>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={regenShare} className="rounded-xl text-stone-600" data-testid="qr-share-regen"><RefreshCw className="h-4 w-4 mr-1.5" /> Nyt link</Button>
                  <Button variant="ghost" size="sm" onClick={deleteShare} className="rounded-xl text-[#A83232] hover:bg-[#F1DEDA]" data-testid="qr-share-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Deaktivér</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center">
              <Button onClick={createShare} data-testid="qr-share-create" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]"><Share2 className="h-4 w-4 mr-1.5" /> Opret link</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={revOpen} onOpenChange={(o) => { setRevOpen(o); if (!o) setRevNote(""); }}>
        <DialogContent data-testid="qr-revision-dialog">
          <DialogHeader>
            <DialogTitle>Ny version</DialogTitle>
            <DialogDescription>Beskriv kort hvad der er ændret. Teksten vises i versionshistorikken på delingssiden.</DialogDescription>
          </DialogHeader>
          <Textarea value={revNote} onChange={(e) => setRevNote(e.target.value)} placeholder="Fx: Tilføjet foto af svejsning + rettet varenr." className="border-[#EBE8E3] rounded-lg bg-white resize-y min-h-[90px]" data-testid="qr-revision-note" autoFocus />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" className="rounded-xl" onClick={() => setRevOpen(false)}>Annullér</Button>
            <Button onClick={bumpRevision} disabled={!revNote.trim()} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="qr-revision-confirm"><Plus className="h-4 w-4 mr-1.5" /> Opret version</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Arkivér rapport?</AlertDialogTitle><AlertDialogDescription>Rapporten forsvinder fra oversigten, men kan gendannes fra arkivet.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annullér</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="qr-delete-confirm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">Arkivér</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, required, filled, children }) => (
  <div className="space-y-1.5">
    <label className="label-caps flex items-center gap-1.5">{label}{required && !filled && <span className="text-[#A83232] font-medium tracking-normal normal-case">· påkrævet</span>}</label>
    {children}
  </div>
);
const IconBtn = ({ children, onClick, danger, title, "data-testid": testId }) => (
  <button data-testid={testId} onClick={onClick} title={title} className={`p-1.5 rounded-md hover:bg-[#F3F1EC] ${danger ? "text-[#A83232]" : "text-stone-500"}`}>{children}</button>
);

const MetaItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 mb-1">{label}</p>
    <p className="text-sm font-medium text-[#1A1919] truncate">{value || "—"}</p>
  </div>
);

function ConclusionBadge({ report }) {
  const map = {
    Godkendt: { c: "#7E856C", bg: "#E7EADF", bd: "#D3D8C6", Icon: CheckCircle2 },
    "Færdig": { c: "#7E856C", bg: "#E7EADF", bd: "#D3D8C6", Icon: CheckCircle2 },
    Afvist: { c: "#A83232", bg: "#F1DEDA", bd: "#E9C9C3", Icon: XCircle },
    "I gang": { c: "#806B61", bg: "#F4EFEC", bd: "#E6DDD6", Icon: Loader2 },
    Kladde: { c: "#78716C", bg: "#F3F1EC", bd: "#E7E5E4", Icon: null },
  };
  const s = map[report.status] || map.Kladde;
  return (
    <span data-testid="qr-conclusion-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: s.bg, color: s.c, border: `1px solid ${s.bd}` }}>
      {s.Icon && <s.Icon className="h-3.5 w-3.5" />}{report.status || "Kladde"}
    </span>
  );
}

function DefectsBlock({ defects = [], ro, onChange }) {  const add = () => onChange([...defects, { id: uid(), description: "", level: "Mindre" }]);
  const upd = (did, patch) => onChange(defects.map((d) => d.id === did ? { ...d, ...patch } : d));
  const rm = (did) => onChange(defects.filter((d) => d.id !== did));
  return (
    <div>
      <div className="flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-[#A83232]" /><span className="font-semibold text-[#1A1919]">Årsager</span></div>
      <div className="space-y-2">
        {defects.map((d) => {
          const lc = LEVELS.find((l) => l.key === d.level)?.color || "#A8A29E";
          return (
            <div key={d.id} className="flex items-center gap-2" data-testid="qr-defect-row">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: lc }} />
              <Input value={d.description} disabled={ro} onChange={(e) => upd(d.id, { description: e.target.value })} placeholder="Beskriv årsagen" className="rounded-lg bg-white border-[#EBE8E3] flex-1" data-testid="qr-defect-desc" />
              <Select value={d.level} disabled={ro} onValueChange={(v) => upd(d.id, { level: v })}>
                <SelectTrigger className="w-32 rounded-lg bg-white border-[#EBE8E3]" data-testid="qr-defect-level"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l.key} value={l.key}>{l.key}</SelectItem>)}</SelectContent>
              </Select>
              {!ro && <button data-testid="qr-defect-remove" onClick={() => rm(d.id)} className="p-1.5 text-[#A83232] hover:bg-[#F1DEDA] rounded-md"><Trash2 className="h-4 w-4" /></button>}
            </div>
          );
        })}
        {defects.length === 0 && <p className="text-sm text-stone-400">Ingen årsager registreret.</p>}
      </div>
      {!ro && <Button variant="ghost" size="sm" onClick={add} className="mt-2 rounded-lg text-[#A83232] hover:bg-[#F1DEDA]" data-testid="qr-add-defect"><Plus className="h-4 w-4 mr-1" /> Tilføj årsag</Button>}
    </div>
  );
}

function AttachmentsBlock({ reportId, attachments = [], ro, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (fl) => {
    const files = Array.from(fl || []); if (!files.length) return;
    setBusy(true);
    try {
      let last = null;
      for (const f of files) { const fd = new FormData(); fd.append("file", f); const r = await api.post(`/quality-reports/${reportId}/attachments`, fd); last = r.data.doc_attachments; }
      if (last) onChange(last);
      toast.success("Dokumentation uploadet");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const del = async (aid) => {
    if (!window.confirm("Fjern denne fil?")) return;
    setBusy(true);
    try { const r = await api.delete(`/quality-reports/${reportId}/attachments/${aid}`); onChange(r.data.doc_attachments || []); toast.success("Fil fjernet"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-[#A83232]" /><span className="font-semibold text-[#1A1919]">Yderligere dokumentation</span></div>
        {!ro && <>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="qr-attach-upload" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-lg text-[#A83232] hover:bg-[#F1DEDA]" data-testid="qr-attach-add"><Upload className="h-4 w-4 mr-1" /> Upload fil</Button>
        </>}
      </div>
      {attachments.length === 0 ? <p className="text-sm text-stone-400">Ingen dokumentation vedhæftet. Fx Word-, Excel-, Outlook- eller PDF-filer.</p> : (
        <div className="space-y-1.5">{attachments.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-lg border border-[#EBE8E3] px-3 py-2 bg-white" data-testid={`qr-attach-${f.id}`}>
            <FileText className="h-4 w-4 text-stone-400 shrink-0" />
            <a href={`${BACKEND}/api/quality-reports/${reportId}/attachments/${f.id}`} target="_blank" rel="noreferrer" className="text-sm text-[#1A1919] hover:underline truncate flex-1">{f.original_filename}</a>
            <span className="text-xs text-stone-400 shrink-0">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</span>
            {!ro && <button onClick={() => del(f.id)} disabled={busy} className="p-1 text-[#A83232] hover:bg-[#F1DEDA] rounded-md" data-testid={`qr-attach-del-${f.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}</div>
      )}
    </div>
  );
}
